import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";

type Placement = "top" | "right" | "bottom" | "left";

export function Tooltip({ label, children, placement = "right", offset = 8, block = false }: {
  label: string;
  children: React.ReactNode;
  placement?: Placement;
  offset?: number;
  block?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const id = useId();

  const pos = (() => {
    // translate classes tailored for each placement
    const off = `${offset}px`;
    switch (placement) {
      case "top":
        return {
          style: { bottom: `calc(100% + ${off})`, left: "50%" },
          cls: "-translate-x-1/2",
        } as const;
      case "bottom":
        return {
          style: { top: `calc(100% + ${off})`, left: "50%" },
          cls: "-translate-x-1/2",
        } as const;
      case "left":
        return {
          style: { right: `calc(100% + ${off})`, top: "50%" },
          cls: "-translate-y-1/2",
        } as const;
      case "right":
      default:
        return {
          style: { left: `calc(100% + ${off})`, top: "50%" },
          cls: "-translate-y-1/2",
        } as const;
    }
  })();

  // Mount animation (enter from slight scale/opacity)
  useEffect(() => {
    if (!open) return setMounted(false);
    const r = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(r);
  }, [open]);

  const tipNode = open ? (
    <span
      id={id}
      className={`pointer-events-none absolute z-50 max-w-[calc(100vw-2rem)] break-words rounded bg-text px-2 py-1 text-xs text-bg shadow transform ${pos.cls} duration-150 transition-opacity transition-transform ${mounted ? "opacity-100 scale-100" : "opacity-0 scale-95"} motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:scale-100`}
      style={pos.style as any}
      role="tooltip"
    >
      {label}
    </span>
  ) : null;

  // Render into body to avoid clipping/overflow issues
  return (
    <span
      className={"relative " + (block ? "block" : "inline-block")}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {typeof document !== "undefined" ? createPortal(tipNode, document.body) : tipNode}
    </span>
  );
}
